import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import lblLogo from './assets/lbl_logo.png';
import viRappImg from './assets/team/vi_rapp.jpg';
import anaComesanaImg from './assets/team/ana_comesana.jpg';
import zhengHeImg from './assets/team/zheng_he.png';
import dylanAnacletoBlackImg from './assets/team/dylan_anacleto-black.jpeg';
import Sidebar from './components/Sidebar';
import Canvas, {
  UNIT as CANVAS_UNIT,
  STOVE_X as CANVAS_STOVE_X,
  STOVE_Y as CANVAS_STOVE_Y,
  STOVE_W as CANVAS_STOVE_W,
  STOVE_H as CANVAS_STOVE_H,
  ROOF_OUTDOOR_DEFAULT,
  WALL_TOP_DEFAULT,
  getItemWorldEndpoints,
  getStoveEndpoints,
} from './components/Canvas';
import Output from './components/Output';
import Settings from './components/Settings';
import ResultsPanel from './components/ResultsPanel';
import { buildSimulationPayload, isDesignNotViable } from './buildPayload';
import { mixedTypeErrors } from './typeConsistency';
import { stovePipeAfterChimneyErrors } from './runOrder';
import { turnsVerticalToHorizontal } from './teeDirection';
import LicenseModal from './components/LicenseModal';
import Assumptions from './components/Assumptions';
import CustomProfileModal from './components/CustomProfileModal';
import { API } from './config/api';
import { findVentOption } from './data';
import useIsMobile from './utils/useIsMobile';
import MobileWorkspace from './components/mobile/MobileWorkspace';
import MobileMenu from './components/mobile/MobileMenu';
import './mobile.css';

// About → Our Team. `photo: null` renders an initials placeholder.
const TEAM = [
  { name: 'Vi Rapp',              photo: viRappImg },
  { name: 'Sharon Chen',          photo: null },
  { name: 'Ana Comesana',         photo: anaComesanaImg },
  { name: 'Zheng He',             photo: zhengHeImg },
  { name: 'Dylan Anacleto-Black', photo: dylanAnacletoBlackImg },
];

// Update this string when shipping a new build.
const LAST_UPDATED = '2026-05-20';

// Single door schedule used by every fire profile: door is wide open until
// 1:30, cracks to 15% (~2 in opening) at 1:35, holds, then closes fully at 3:00.
// Each profile differs only in its inlet-temp curve.
const DEFAULT_DOOR_SCHEDULE = [
  [0,   100],
  [90,  100],
  [95,  15],
  [175, 15],
  [180, 0],
];

// Inlet-temperature profiles below are sampled from the canonical 3-phase fire
// curves in mc-simulation-tool/notebooks/default_fire_curves.ipynb (power-law rise
// -> fast exponential drop -> slow exponential tail). Each profile shares the
// same shape; only T_max, rise duration, and tail temperatures differ.
//   Low    <- cool_start   (peak 400 C @ 4.4 min, settles to 280 C)
//   Medium <- normal_start (peak 600 C @ 3.9 min, settles to 420 C)
//   High   <- hot_start    (peak 700 C @ 3.4 min, settles to 450 C)
// Values are in Kelvin; gas starts at ambient (20 C = 293 K) before kindling.
const FIRE_PROFILES = {
  long_burn: {
    label: 'Low',
    doorSchedule: DEFAULT_DOOR_SCHEDULE,
    inletTempProfile: [
      [0,    293],
      [60,   293],
      [84,   293],
      [120,  300],
      [264,  673],
      [294,  592],
      [360,  574],
      [420,  569],
      [480,  566],
      [600,  561],
      [900,  555],
      [1200, 554],
      [1800, 553],
    ],
  },
  balanced: {
    label: 'Medium',
    doorSchedule: DEFAULT_DOOR_SCHEDULE,
    inletTempProfile: [
      [0,    293],
      [60,   293],
      [84,   293],
      [120,  310],
      [234,  873],
      [264,  752],
      [330,  724],
      [390,  717],
      [480,  710],
      [600,  703],
      [900,  696],
      [1200, 694],
      [1800, 693],
    ],
  },
  high_output: {
    label: 'High',
    doorSchedule: DEFAULT_DOOR_SCHEDULE,
    inletTempProfile: [
      [0,    293],
      [60,   293],
      [84,   293],
      [120,  327],
      [204,  973],
      [234,  811],
      [300,  774],
      [360,  763],
      [480,  747],
      [600,  738],
      [900,  727],
      [1200, 724],
      [1800, 723],
    ],
  },
};

// Sidebar → Templates. Picking one and clicking Insert replaces the canvas
// with a ready-made layout. Each builder returns an array of canvas items laid
// out relative to the stove via the ctx it's handed (so positions track the
// live stove location). Item shape matches what Sidebar drops / handlePresetSelect
// builds: { name, type, componentType, diameters, selectedDiameter, orientation,
// length, height, rotation, rotationStep, x, y, ... }.
const TEMPLATE_OPTIONS = [
  { value: 'straight-chimney', label: 'Straight Chimney' },
  { value: 'bent-chimney',     label: 'Bent Chimney' },
  { value: 'lined-chimney',    label: 'Lined Chimney' },
];

// ctx = { UNIT, stoveX, stoveY, STOVE_W, STOVE_H }
function buildTemplateItems(templateId, ctx) {
  const { UNIT, stoveX, stoveY, STOVE_W, STOVE_H } = ctx;
  // Vertical pixel height of a straight pipe of `lenFt` feet — mirrors Canvas
  // getItemSize (STRAIGHT_PIPE_BASE_LEN = UNIT*0.7; height = base * floor(len)).
  const pipeH = (lenFt) => UNIT * 0.7 * Math.max(1, Math.floor(lenFt));
  // Center the column on the stove flue; nudge the first part down onto the
  // stove's visible flue collar (transparent top margin) so it isn't gapped.
  const colX = stoveX + STOVE_W / 2 - UNIT / 2;
  const stoveTopInset = UNIT * 0.7 / 4 + 5;
  // Vertical straight pipe at rotation 0 (current pipe.svg is drawn vertical).
  // Temps deliberately omitted so the user must enter Surrounding Temp per part.
  const vStraight = (overrides) => ({
    diameters: ['6'], selectedDiameter: '6', orientation: 'vertical',
    rotation: 0, rotationStep: 15, type: 'Pipe', ...overrides,
  });

  switch (templateId) {
    case 'straight-chimney': {
      // 8 ft single-wall steel stove pipe → 7 ft Class A stainless chimney → cap,
      // stacked straight up from the stove.
      const h1 = pipeH(8);
      const h2 = pipeH(7);
      const y1 = stoveY + stoveTopInset - h1;   // part 1 bottom on the stove collar
      const y2 = y1 - h2;                        // part 2 sits on part 1
      return [
        vStraight({
          name: 'Straight Pipe', componentType: 'pipe-straight',
          length: 8, height: 8, ventOptionId: 'stovepipe-carbon-steel',
          x: colX, y: y1,
        }),
        vStraight({
          name: 'Chimney Straight', componentType: 'chimney-straight',
          length: 7, height: 7, ventOptionId: 'chimney-class-a-stainless',
          x: colX, y: y2,
        }),
        {
          name: 'Cap', type: 'Elbow', componentType: 'cap',
          diameters: ['6'], selectedDiameter: '6',
          height: 1, rotation: 0, rotationStep: 90,
          x: colX, y: y2 - UNIT,                // cap bottom on part 2 top
        },
      ];
    }
    case 'bent-chimney': {
      // 5.5 ft single-wall stainless stove pipe (vertical) → 90° stove-pipe
      // elbow → 2 ft horizontal Class A → chimney tee (turns flow up) → 6 ft
      // vertical Class A → cap. Parts are chained so each port lands exactly on
      // the previous one's. Port offsets are fractions of UNIT taken from the
      // SVG port geometry in Canvas.js ENDPOINTS_BY_TYPE.
      const U = UNIT;
      const cX = stoveX + STOVE_W / 2;          // stove flue center x

      // 1) vertical single-wall stainless stove pipe, bottom on the stove collar.
      const h1 = pipeH(5.5);
      const p1y = stoveY + stoveTopInset - h1;
      const part1 = vStraight({
        name: 'Straight Pipe', componentType: 'pipe-straight',
        length: 5.5, height: 5.5, ventOptionId: 'stovepipe-stainless',
        x: cX - U / 2, y: p1y,
      });
      const top1 = { x: cX, y: p1y };                                   // part1 top port

      // 2) 90° stove-pipe elbow: input (bottom) on part1 top, output to the right.
      const elbow = {
        name: 'Elbow 90°', type: 'Elbow', componentType: 'elbow-90',
        diameters: ['6'], selectedDiameter: '6', orientation: 'vertical',
        height: 1, rotation: 0, rotationStep: 90,
        x: top1.x - 0.504 * U, y: top1.y - 0.972 * U,
      };
      const out2 = { x: elbow.x + 0.973 * U, y: elbow.y + 0.5035 * U }; // elbow right port

      // 3) horizontal Class A chimney, left port on the elbow output.
      const h3 = pipeH(2);
      const part3 = vStraight({
        name: 'Chimney Straight', componentType: 'chimney-straight',
        orientation: 'horizontal', rotation: 90,
        length: 2, height: 2, ventOptionId: 'chimney-class-a-stainless',
        x: out2.x - (U / 2 - h3 / 2), y: out2.y - h3 / 2,
      });
      const out3 = { x: part3.x + (U / 2 + h3 / 2), y: part3.y + h3 / 2 }; // part3 right port

      // 4) chimney tee: branch (left) on part3 output, through-flow turns up.
      const tee = {
        name: 'Chimney Tee', type: 'Elbow', componentType: 'chimney-tee',
        diameters: ['6'], selectedDiameter: '6', orientation: 'vertical',
        height: 1, rotation: 0, rotationStep: 90,
        x: out3.x - (U / 2 - 0.517 * U), y: out3.y - U / 2,
      };
      const top4 = { x: tee.x + U / 2, y: tee.y + (U / 2 - 0.517 * U) }; // tee top port

      // 5) vertical Class A chimney, bottom on the tee top.
      const h5 = pipeH(6);
      const part5 = vStraight({
        name: 'Chimney Straight', componentType: 'chimney-straight',
        length: 6, height: 6, ventOptionId: 'chimney-class-a-stainless',
        x: top4.x - U / 2, y: top4.y - h5,
      });

      // 6) cap on part5 top.
      const cap = {
        name: 'Cap', type: 'Elbow', componentType: 'cap',
        diameters: ['6'], selectedDiameter: '6',
        height: 1, rotation: 0, rotationStep: 90,
        x: part5.x, y: part5.y - U,
      };
      return [part1, elbow, part3, tee, part5, cap];
    }
    case 'lined-chimney': {
      // Masonry tee off the stove's side (branch = side-entry, turns flow up) →
      // 15 ft vertical lined-masonry straight with vermiculite insulation → cap.
      // The tee's bottom leg is left open as a cleanout.
      const U = UNIT;
      // Branch (left) port docks onto the stove's right-side mid endpoint.
      const sideTarget = { x: stoveX + STOVE_W, y: stoveY + STOVE_H / 2 };

      // 1) masonry tee, branch on the stove side.
      const tee = {
        name: 'Masonry Tee', type: 'Elbow', componentType: 'masonry-tee',
        diameters: ['6'], selectedDiameter: '6', orientation: 'vertical',
        height: 1, rotation: 0, rotationStep: 90,
        x: sideTarget.x - (U / 2 - 0.517 * U), y: sideTarget.y - U / 2,
      };
      const top1 = { x: tee.x + U / 2, y: tee.y + (U / 2 - 0.517 * U) }; // tee top port

      // 2) 15 ft vertical lined-masonry straight (vermiculite), bottom on tee top.
      const h2 = pipeH(15);
      const straight = vStraight({
        name: 'Masonry Straight', componentType: 'masonry-straight',
        length: 15, height: 15, ventOptionId: 'lined-masonry-vermiculite',
        x: top1.x - U / 2, y: top1.y - h2,
      });

      // 3) cap on the straight top.
      const cap = {
        name: 'Cap', type: 'Elbow', componentType: 'cap',
        diameters: ['6'], selectedDiameter: '6',
        height: 1, rotation: 0, rotationStep: 90,
        x: straight.x, y: straight.y - U,
      };
      return [tee, straight, cap];
    }
    default:
      return [];
  }
}

// Shown under About → Disclaimers. Rendered verbatim (white-space: pre-wrap).
const DISCLAIMERS_TEXT = `*** Copyright Notice ***

MC Chimney Draft Tool (Draft Tool) Copyright (c) 2026, The Regents of the University of California, through Lawrence Berkeley National Laboratory (subject to receipt of any required approvals from the U.S. Dept. of Energy). All rights reserved.

If you have questions about your rights to use or distribute this software, please contact Berkeley Lab's Intellectual Property Office at IPO@lbl.gov.

NOTICE.  This Software was developed under funding from the U.S. Department of Energy and the U.S. Government consequently retains certain rights. As such, the U.S. Government has been granted for itself and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in the Software to reproduce, distribute copies to the public, prepare derivative works, and perform publicly and display publicly, and to permit others to do so.

*** License Agreement ***

MC Chimney Draft Tool (Draft Tool) Copyright (c) 2026, The Regents of the University of California, through Lawrence Berkeley National Laboratory (subject to receipt of any required approvals from the U.S. Dept. of Energy). All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:

(1) Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.

(2) Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.

(3) Neither the name of the University of California, Lawrence Berkeley National Laboratory, U.S. Dept. of Energy nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.


THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.

You are under no obligation whatsoever to provide any bug fixes, patches, or upgrades to the features, functionality or performance of the source code ("Enhancements") to anyone; however, if you choose to make your Enhancements available either publicly, or directly to Lawrence Berkeley National Laboratory, without imposing a separate written license agreement for such Enhancements, then you hereby grant the following license: a non-exclusive, royalty-free perpetual license to install, use, modify, prepare derivative works, incorporate into other computer software, distribute, and sublicense such enhancements or derivative works thereof, in binary and source code form.`;

function App() {

  // License agreement gate — the modal blocks all interaction until accepted.
  // Acceptance persists in localStorage so the user only sees it on first
  // visit per browser. Bump the version suffix on the storage key if the
  // license text changes and you need everyone to re-accept.
  const LICENSE_STORAGE_KEY = 'mc-chimney-draft-tool-license-accepted-v1';
  const [licenseAccepted, setLicenseAccepted] = useState(() => {
    try {
      return window.localStorage.getItem(LICENSE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const handleLicenseAgree = () => {
    try {
      window.localStorage.setItem(LICENSE_STORAGE_KEY, 'true');
    } catch {
      /* private mode / storage blocked — just proceed for this session */
    }
    setLicenseAccepted(true);
  };

  const [activeTab, setActiveTab] = useState('workspace');
  const [aboutSubtab, setAboutSubtab] = useState('team');
  const [guideSubtab, setGuideSubtab] = useState('guide');
  const [canvasItems, setCanvasItems] = useState([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [showComponentList, setShowComponentList] = useState(false);
  const [showStoveMenu, setShowStoveMenu] = useState(false);
  const [doorAngle, setDoorAngle] = useState(0);
  const [payloadText, setPayloadText] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [resultUnits, setResultUnits] = useState('imperial');
  const [indoorTempF, setIndoorTempF] = useState('65');
  const [outdoorTempF, setOutdoorTempF] = useState('35');
  const [elevationFt, setElevationFt] = useState('0');
  const [depressurizationPa, setDepressurizationPa] = useState('0');
  const [outputError, setOutputError] = useState(null);
  const [outputStatus, setOutputStatus] = useState(null);
  const [selectedFireProfile, setSelectedFireProfile] = useState('balanced');
  // User-uploaded inlet-temperature profile: { points: [[t_s, T_K]], name } or null.
  // When the Fire Profile dropdown is set to 'custom' this overrides the presets.
  const [customProfile, setCustomProfile] = useState(null);
  // null = closed; 'intro' | 'upload' = the open step of the custom-profile modal.
  const [customModalStep, setCustomModalStep] = useState(null);
  const [stoveX, setStoveX] = useState(CANVAS_STOVE_X);
  // Tracked because Canvas anchors the stove to the bottom of the current
  // canvas height (which grows with the window). Validation needs the live
  // value, not the static CANVAS_STOVE_Y from the imports.
  const [stoveY, setStoveY] = useState(CANVAS_STOVE_Y);
  const [roofOutdoorOverhang, setRoofOutdoorOverhang] = useState(ROOF_OUTDOOR_DEFAULT);
  const [wallTopY, setWallTopY] = useState(WALL_TOP_DEFAULT);

  // Phone-only UI switch (touch + small screen, or ?mobile=1). When true, the
  // workspace and top-bar render their phone variants; desktop is untouched.
  const isMobile = useIsMobile();

  // Invalidate cached results whenever any simulation input changes so the
  // user can't view stale results that no longer match the current design.
  // Skip the very first render so a result loaded at mount (e.g. MOCK_RESULT)
  // isn't cleared before the user sees it.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (lastResult) {
      setLastResult(null);
      setShowResults(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canvasItems,
    selectedFireProfile,
    customProfile,
    elevationFt,
    indoorTempF,
    outdoorTempF,
    stoveX,
    roofOutdoorOverhang,
    wallTopY,
  ]);

  // Fire Profile dropdown handler. Choosing "Custom…" opens the upload modal
  // (intro step if nothing's loaded yet, straight to upload when editing);
  // choosing a preset clears any custom profile.
  const handleFireProfileChange = (value) => {
    if (value === 'custom') {
      setCustomModalStep(customProfile ? 'upload' : 'intro');
      return; // selection only switches to 'custom' once a profile is applied
    }
    setSelectedFireProfile(value);
    setCustomProfile(null);
  };

  const handleUseCustomProfile = (profile) => {
    setCustomProfile(profile);
    setSelectedFireProfile('custom');
    setCustomModalStep(null);
  };

  const handleSeeCustomInstructions = () => {
    setCustomModalStep(null);
    setActiveTab('guide');
    setGuideSubtab('guide');
  };

  const handleCalculateSuccess = (data) => {
    setLastResult(data);
    setShowResults(true);
    setOutputError(null);
    setOutputStatus(null);
  };

  const runSimulation = async (payload) => {
    const response = await fetch(API.simulate, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || JSON.stringify(data));
    if (data && data.ok === false) {
      throw new Error(data.error || data.detail || 'Simulation failed');
    }

    console.group('[simulate] backend response');
    console.log('ul103_risk:', data.ul103_risk);
    console.log('backdraft_risk:', data.backdraft_risk);
    console.log('max_static_pressure_ss_Pa:', data.max_static_pressure_ss_Pa);
    console.log('max_temperature_ss_C:', data.max_temperature_ss_C);
    console.log('static_pressure_plot:', data.static_pressure_plot);
    console.log('temperature_plot:', data.temperature_plot);
    console.log('full payload:', data);
    console.groupEnd();

    handleCalculateSuccess(data);
    return data;
  };

  // Parse the current JSON payload, apply changes to parts[index], re-serialize
  const updatePayloadPart = (index, changes) => {
    setPayloadText(prev => {
      if (!prev.trim()) return prev;
      try {
        const config = JSON.parse(prev);
        if (!config.parts || config.parts[index] === undefined) return prev;
        config.parts = config.parts.map((p, i) => i === index ? { ...p, ...changes } : p);
        return JSON.stringify(config, null, 2);
      } catch {
        return prev; // leave unchanged if JSON is invalid
      }
    });
  };

  const handleDiametersChange = (index, selectedOptions) => {
    const newDiameters = Array.from(selectedOptions, option => option.value);
    const newItems = [...canvasItems];
    newItems[index].diameters = newDiameters;
    newItems[index].selectedDiameter = newDiameters[0];
    setCanvasItems(newItems);
    updatePayloadPart(index, { inner_diameter_ft: parseFloat(newDiameters[0]) / 12 });
  };

  const ORIENTATION_TO_ROTATION = {
    'vertical': 0,
    'horizontal': 90,
    '30': 30,
    '45': 45,
  };

  const handleOrientationChange = (index, selectedOption) => {
    setCanvasItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      // Manual orientation change clears the snap-driven rotation flag, so a
      // later drag-away won't revert this rotation back to vertical.
      const updated = { ...item, orientation: selectedOption, _snapRotated: null };
      if (item.componentType === 'pipe-straight' || item.componentType === 'chimney-straight' || item.componentType === 'masonry-straight') {
        updated.rotation = ORIENTATION_TO_ROTATION[selectedOption] ?? 0;
      }
      return updated;
    }));
    updatePayloadPart(index, { orientation: selectedOption });
  };

  // Build the orientation dropdown options for a pipe based on whether it's
  // touching a 30°/45° elbow (endpoint-adjacent). Vertical/Horizontal always
  // show; angled options appear only when the geometry calls for them.
  const getAvailableOrientations = (item) => {
    if (!item) return [];
    if (item.componentType !== 'pipe-straight' && item.componentType !== 'chimney-straight' && item.componentType !== 'masonry-straight') {
      return [];
    }
    const EP_TOL = 8;
    const itemEPs = getItemWorldEndpoints(item);
    const opts = [
      { value: 'vertical',   label: 'Vertical' },
      { value: 'horizontal', label: 'Horizontal' },
    ];
    let has45 = false, has30 = false;
    for (const other of canvasItems) {
      if (other === item) continue;
      const ct = other.componentType;
      if (ct !== 'elbow-45' && ct !== 'chimney-30') continue;
      const otherEPs = getItemWorldEndpoints(other);
      const touching = itemEPs.some(p => otherEPs.some(q =>
        Math.hypot(p.x - q.x, p.y - q.y) <= EP_TOL
      ));
      if (touching) {
        if (ct === 'elbow-45') has45 = true;
        if (ct === 'chimney-30') has30 = true;
      }
    }
    if (has30) opts.push({ value: '30', label: '30°' });
    if (has45) opts.push({ value: '45', label: '45°' });
    return opts;
  };

  const handleHeightChange = (index, selectedValue) => {
    const parsed = parseFloat(selectedValue);
    const newItems = [...canvasItems];
    // Keep both keys for backward compatibility; Canvas prefers `length`.
    newItems[index].length = parsed;
    newItems[index].height = parsed;
    setCanvasItems(newItems);
    updatePayloadPart(index, { height_ft: parsed });
  };

  // When the user picks a vent type from the Output dropdown, store the
  // id on the item. Payload assembly (below) uses the id to look up the
  // option's material properties from ventOptions in data.js.
  const handleVentTypeChange = (index, ventOptionId) => {
    setCanvasItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      return { ...item, ventOptionId: ventOptionId || undefined };
    }));
  };

  const handleAmbientTempChange = (index, selectedValue) => {
    const trimmed = String(selectedValue).trim();
    const parsed = trimmed === '' ? undefined : Number(trimmed);

    setCanvasItems(prev => prev.map((item, i) => {
      if (i !== index) return item;

      // Empty/invalid input -> blank (user must specify before Finalize).
      if (parsed == null || Number.isNaN(parsed)) {
        return {
          ...item,
          ambientTempF: undefined,
          ambientTempOverride: false,
        };
      }

      return {
        ...item,
        ambientTempF: parsed,
        ambientTempOverride: true,
      };
    }));
  };

  const handleSequenceOrderChange = (index, selectedValue) => {
    const trimmed = String(selectedValue).trim();
    const parsed = trimmed === '' ? undefined : Number(trimmed);

    setCanvasItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      if (parsed == null || Number.isNaN(parsed) || parsed < 1) {
        const { sequenceOrder, manualSequenceOrder, ...rest } = item;
        return rest;
      }
      const n = Math.round(parsed);
      return { ...item, sequenceOrder: n, manualSequenceOrder: n };
    }));
  };

  const handleDelete = (index) => {
    const removedSequence = canvasItems[index]?.sequenceOrder;

    setCanvasItems(prev => prev
      .filter((_, i) => i !== index)
      .map(item => {
        if (typeof removedSequence !== 'number') return item;
        const updated = { ...item };
        if (typeof item.sequenceOrder === 'number' && item.sequenceOrder > removedSequence) {
          updated.sequenceOrder = item.sequenceOrder - 1;
        }
        if (typeof item.manualSequenceOrder === 'number' && item.manualSequenceOrder > removedSequence) {
          updated.manualSequenceOrder = item.manualSequenceOrder - 1;
        }
        return updated;
      }));

    if (selectedItemIndex === index) {
      setSelectedItemIndex(null);
    } else if (selectedItemIndex > index) {
      setSelectedItemIndex(selectedItemIndex - 1);
    }
  };

  const handleClearDesign = () => {
    setCanvasItems([]);
    setSelectedItemIndex(null);
    setShowComponentList(false);
    setPayloadText('');
    setLastResult(null);
    setShowResults(false);
    setOutputError(null);
    setOutputStatus(null);
  };

  const handlePresetSelect = (config) => {
    const UNIT   = CANVAS_UNIT;
    const parts  = config.parts  || [];
    const elbows = config.elbows || [];

    // Map partIndex → the elbow just before it (between_parts[1] === partIndex)
    const elbowBefore = {};
    elbows.forEach(e => { elbowBefore[e.between_parts[1]] = e; });

    // Build interleaved sequence: [fitting-before-0, pipe-0, fitting-before-1, pipe-1, ...]
    const sequence = [];
    if (elbowBefore[0]) sequence.push({ kind: 'elbow', data: elbowBefore[0] });
    parts.forEach((part, idx) => {
      sequence.push({ kind: 'pipe', data: part });
      if (elbowBefore[idx + 1]) sequence.push({ kind: 'elbow', data: elbowBefore[idx + 1] });
    });

    // "After direction" for item i = direction of travel leaving item i.
    // Pipes use their own orientation; fittings inherit from the next pipe.
    const getAfterDir = (i) => {
      for (let j = i; j < sequence.length; j++) {
        if (sequence[j].kind === 'pipe')
          return sequence[j].data.orientation === 'horizontal' ? 'right' : 'up';
      }
      return 'up';
    };

    // Start cursor: one cell directly above the stove, horizontally centred on it
    let cursorX = Math.round(CANVAS_STOVE_X + CANVAS_STOVE_W / 2 - UNIT / 2);
    let cursorY = stoveY - UNIT;

    const newItems = [];

    sequence.forEach(({ kind, data }, i) => {
      const rawDiam    = data.inner_diameter_ft || data.diameter_ft || 0.5;
      const diamInches = Math.round(rawDiam * 12);
      const afterDir   = getAfterDir(i);
      const beforeDir  = i === 0 ? 'up' : getAfterDir(i - 1);

      if (kind === 'pipe') {
        newItems.push({
          name:             data._name || 'Stove Pipe',
          type:             'Pipe',
          componentType:    'pipe-straight',
          diameters:        [String(diamInches)],
          selectedDiameter: String(diamInches),
          orientation:      afterDir === 'right' ? 'horizontal' : 'vertical',
          length:           data.height_ft || 3,
          height:           data.height_ft || 3,
          // pipe.svg is horizontal; rotate 90° so vertical pipes appear upright
          rotation:         afterDir === 'up' ? 90 : 0,
          rotationStep:     90,
          x: cursorX,
          y: cursorY,
        });
      } else {
        const isTee = data.type === 'tee';
        const angleDeg = Number(data.angle_deg) || 90;
        const isFortyFive = !isTee && angleDeg === 45;
        // Rotation so the fitting visually matches the flow:
        //   tee  up→up    = -90°   tee  right→up  = -90° + 90° = 0°
        //   elbow up→right = 90°   elbow right→up  = 90° + 90° = 180°
        let rotation = isTee ? -90 : 90;
        if ( isTee && beforeDir === 'right')                       rotation = 0;
        if (!isTee && beforeDir === 'right' && afterDir === 'up')  rotation = 180;
        newItems.push({
          name:             isTee ? 'Tee' : (isFortyFive ? 'Elbow 45°' : 'Elbow 90°'),
          type:             'Elbow',
          componentType:    isTee ? 'tee' : (isFortyFive ? 'elbow-45' : 'elbow-90'),
          diameters:        [String(diamInches)],
          selectedDiameter: String(diamInches),
          orientation:      beforeDir === 'right' ? 'horizontal' : 'vertical',
          height:           data.height_ft || data.vertical_length_ft || 1,
          rotation,
          rotationStep:     isFortyFive ? 45 : 90,
          angleLabel:       isFortyFive ? '45°' : undefined,
          x: cursorX,
          y: cursorY,
        });
      }

      // Advance cursor one grid cell in the outgoing direction
      if (afterDir === 'up') cursorY -= UNIT;
      else                   cursorX += UNIT;
    });

    setCanvasItems(newItems);
    setSelectedItemIndex(null);
    setShowComponentList(false);
    setPayloadText(JSON.stringify(config, null, 2));
  };

  const handleInsertTemplate = (templateId) => {
    const items = buildTemplateItems(templateId, {
      UNIT: CANVAS_UNIT,
      stoveX,
      stoveY,
      STOVE_W: CANVAS_STOVE_W,
      STOVE_H: CANVAS_STOVE_H,
    });
    if (!items || items.length === 0) {
      setOutputError('That template is not defined yet.');
      return;
    }
    setCanvasItems(items);
    setSelectedItemIndex(null);
    setShowComponentList(false);
    setPayloadText('');
    setLastResult(null);
    setShowResults(false);
    setOutputError(null);
    setOutputStatus(null);
  };

  // Phone tap-to-place: append a part from the catalog onto the canvas. It drops
  // just above the current topmost part (or the stove) so it's visible near where
  // it connects; the user then finger-drags it to snap. Mirrors the field set the
  // desktop drop handler (Canvas.handleDropFromSidebar) normalises.
  const handleAddPart = (template) => {
    const UNIT = CANVAS_UNIT;
    const colX = stoveX + CANVAS_STOVE_W / 2 - UNIT / 2;
    const topY = canvasItems.reduce(
      (min, it) => Math.min(min, Number.isFinite(it.y) ? it.y : Infinity),
      stoveY,
    );
    const newItem = {
      ...template,
      selectedDiameter: template.selectedDiameter || template.diameters?.[0] || '6',
      orientation: template.orientation || 'vertical',
      length: template.length ?? template.height ?? 1,
      height: template.height ?? template.length ?? 1,
      rotation: template.rotation ?? 0,
      rotationStep: template.rotationStep || 90,
      x: colX,
      y: topY - UNIT,
    };
    setCanvasItems(prev => [...prev, newItem]);
  };

  const handleDoorAngleChange = (angle) => {
    setDoorAngle(angle);
  };

  const hasParts = canvasItems.length > 0;
  const canFinalizeDesign = hasParts;

  // Auto-number parts by walking the endpoint-connectivity graph from the stove.
  // Two items are connected iff one of their world endpoints sits within EP_TOL of
  // an endpoint of the other. Cap is pinned to the very last position. Items with
  // a manual `sequenceOrder` keep that number; remaining items fill the gaps in
  // visit order.
  const autoNumberItems = (items) => {
    const N = items.length;
    if (N === 0) return items;

    const EP_TOL = 8; // px tolerance for "endpoints touch"
    const itemEPs = items.map(it => getItemWorldEndpoints(it));
    const stoveEPs = getStoveEndpoints(
      stoveX, stoveY, CANVAS_STOVE_W, CANVAS_STOVE_H,
    );

    const close = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) <= EP_TOL;

    // Build adjacency between items.
    const adj = items.map(() => new Set());
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const touching = itemEPs[i].some(p => itemEPs[j].some(q => close(p, q)));
        if (touching) {
          adj[i].add(j);
          adj[j].add(i);
        }
      }
    }

    // Find item(s) whose endpoint touches a stove endpoint. Pick the closest as start.
    let start = -1;
    let startD = Infinity;
    for (let i = 0; i < N; i++) {
      for (const ep of itemEPs[i]) {
        for (const sep of stoveEPs) {
          const d = Math.hypot(ep.x - sep.x, ep.y - sep.y);
          if (d < startD) { startD = d; start = i; }
        }
      }
    }

    // BFS from start in path order. Within each adjacency expansion, prefer
    // non-cap successors so the cap is naturally visited last.
    const visited = new Array(N).fill(false);
    const order = [];
    if (start >= 0) {
      const queue = [start];
      visited[start] = true;
      while (queue.length) {
        const cur = queue.shift();
        order.push(cur);
        const successors = [...adj[cur]].filter(j => !visited[j]);
        successors.sort((a, b) => {
          const aCap = items[a].componentType === 'cap';
          const bCap = items[b].componentType === 'cap';
          if (aCap !== bCap) return aCap ? 1 : -1;
          return 0;
        });
        for (const nxt of successors) {
          visited[nxt] = true;
          queue.push(nxt);
        }
      }
    }

    // Append any orphans (parts not reached by the walk). Cap pinned to the
    // very end if it didn't come up naturally.
    for (let i = 0; i < N; i++) {
      if (!visited[i]) order.push(i);
    }
    const capOrderIdx = order.findIndex(i => items[i].componentType === 'cap');
    if (capOrderIdx >= 0 && capOrderIdx !== order.length - 1) {
      const [capIdx] = order.splice(capOrderIdx, 1);
      order.push(capIdx);
    }

    // The cap is always pinned to the last position (N), overriding any manual
    // override the user may have typed. Other parts' user-typed manual numbers
    // stay put; auto-assigned numbers from a previous Finalize are recomputed.
    const capItemIdx = items.findIndex(it => it.componentType === 'cap');

    const reserved = new Set();
    items.forEach((it, i) => {
      if (i === capItemIdx) return;
      if (Number.isFinite(it.manualSequenceOrder) && it.manualSequenceOrder !== N) {
        reserved.add(it.manualSequenceOrder);
      }
    });
    if (capItemIdx >= 0) reserved.add(N);

    const assigned = new Map();
    let next = 1;
    for (const i of order) {
      if (i === capItemIdx) continue;
      if (Number.isFinite(items[i].manualSequenceOrder) && items[i].manualSequenceOrder !== N) continue;
      while (reserved.has(next)) next += 1;
      assigned.set(i, next);
      next += 1;
    }
    if (capItemIdx >= 0) assigned.set(capItemIdx, N);

    return items.map((item, i) => {
      if (i === capItemIdx) return { ...item, sequenceOrder: N };
      if (Number.isFinite(item.manualSequenceOrder) && item.manualSequenceOrder !== N) {
        return { ...item, sequenceOrder: item.manualSequenceOrder };
      }
      return assigned.has(i) ? { ...item, sequenceOrder: assigned.get(i) } : item;
    });
  };

  const handleFinalizeDesign = async () => {
    setOutputError(null);
    setOutputStatus(null);
    setSelectedItemIndex(null);

    if (canvasItems.length === 0) {
      setOutputError('No parts on the canvas.');
      return;
    }

    if (selectedFireProfile === 'custom' && !customProfile) {
      setOutputError('Upload a custom fire profile first (Fire Profile → Custom…).');
      return;
    }

    const numberedItems = autoNumberItems(canvasItems);
    setCanvasItems(numberedItems);

    const orderedForValidation = [...numberedItems].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    const partItemsForValidation = orderedForValidation.filter(
      item => item.componentType === 'pipe-straight' || item.componentType === 'chimney-straight' || item.componentType === 'masonry-straight',
    );

    const errors = [];

    const sequenceNumbers = numberedItems
      .map(item => item.sequenceOrder)
      .sort((a, b) => a - b);
    const hasDuplicates = sequenceNumbers.some((n, idx) => idx > 0 && n === sequenceNumbers[idx - 1]);
    if (hasDuplicates) {
      errors.push('Two or more parts share the same Part Number. Adjust the manual overrides.');
    }
    const numberingIsContiguous = sequenceNumbers.every((n, idx) => n === idx + 1);
    if (!hasDuplicates && !numberingIsContiguous) {
      errors.push('Part numbering is invalid. Manual Part Numbers must be in the range 1..N with no gaps.');
    }

    if (partItemsForValidation.length === 0) {
      errors.push('No straight pipe/chimney segments are present in the design.');
    }

    const capCount = orderedForValidation.filter(item => item.componentType === 'cap').length;
    if (capCount === 0) {
      errors.push('Add a chimney cap at the top of the chimney.');
    } else if (capCount > 1) {
      errors.push(`The chimney can only have one cap (found ${capCount}). Remove the extras.`);
    }

    // The chimney must terminate vertically. We trace flow through the path,
    // picking each fitting's input port (the one whose outward direction best
    // matches the incoming flow) and treating the OTHER port as the output —
    // so the same elbow can serve as a CW turn or a CCW turn depending on the
    // user's chosen rotation. Two 45° turns oriented oppositely (S-bend) net
    // to 0° and pass; a lone 30° elbow leaves the flow 30° off-vertical and
    // errors. Straight pipes don't change direction.
    const VERTICAL_TOLERANCE_DEG = 10;
    // Intrinsic port outward directions per component type (unit vectors,
    // canvas y-down). The first port is conventionally the "input" port from
    // below, but at runtime we pick whichever port best matches the incoming
    // flow — this is what makes elbows bidirectional in the trace.
    const INTRINSIC_PORTS = {
      'pipe-straight':  [{ x: 0, y: -1 }, { x: 0, y: 1 }],   // top, bottom
      'chimney-straight': [{ x: 0, y: -1 }, { x: 0, y: 1 }],
      'masonry-straight': [{ x: 0, y: -1 }, { x: 0, y: 1 }],
      'elbow-90':       [{ x: 0, y: 1 }, { x: 1, y: 0 }],    // bottom, right
      'elbow-45':       [{ x: 0, y: 1 }, { x: Math.SQRT1_2, y: -Math.SQRT1_2 }],   // bottom, 45° up-right
      'chimney-30':     [{ x: 0, y: 1 }, { x: 0.5, y: -Math.cos(Math.PI / 6) }],   // bottom, 30° from vertical up-right
      // Tee has 3 ports — straight-through (bottom→top) by default; if the
      // user wires it as a 90° branch, the "best-match input + farthest-from-
      // input output" picks the branch port.
      'tee':            [{ x: 0, y: 1 }, { x: 0, y: -1 }, { x: -1, y: 0 }],
      'chimney-tee':    [{ x: 0, y: 1 }, { x: 0, y: -1 }, { x: -1, y: 0 }],
      'masonry-tee':    [{ x: 0, y: 1 }, { x: 0, y: -1 }, { x: -1, y: 0 }],
    };
    const rotateVec = (v, deg) => {
      const r = (deg * Math.PI) / 180;
      const c = Math.cos(r), s = Math.sin(r);
      return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
    };
    // Initial flow direction depends on where part 1 attaches to the stove:
    // top of stove -> flow UP, left side -> flow LEFT, right side -> RIGHT.
    // The path must still terminate vertically (cap pointing up) regardless
    // of where it started, so this only changes the starting frame for the
    // trace; the same end-vertical check applies.
    let flowDir = { x: 0, y: -1 };
    const firstPart = orderedForValidation.find(it => it.componentType !== 'cap');
    if (firstPart) {
      const stoveCenterX = stoveX + CANVAS_STOVE_W / 2;
      const stoveCenterY = stoveY + CANVAS_STOVE_H / 2;
      const partCenterX = (firstPart.x ?? 0) + CANVAS_UNIT / 2;
      const partCenterY = (firstPart.y ?? 0) + CANVAS_UNIT / 2;
      const dx = partCenterX - stoveCenterX;
      const dy = partCenterY - stoveCenterY;
      if (Math.abs(dx) > Math.abs(dy)) {
        flowDir = { x: dx > 0 ? 1 : -1, y: 0 };   // side-mounted
      } else {
        flowDir = { x: 0, y: dy > 0 ? 1 : -1 };   // top (dy<0) or bottom
      }
    }
    const angleFromVertical = (v) => {
      // Angle between v and (0, -1) (up), in degrees, [0, 180].
      const dot = -v.y;  // dot product with (0, -1)
      const mag = Math.hypot(v.x, v.y) || 1;
      return Math.acos(Math.max(-1, Math.min(1, dot / mag))) * 180 / Math.PI;
    };
    let downwardFlowError = null;
    let teeDirectionError = null;
    for (const it of orderedForValidation) {
      const ct = it.componentType;
      if (ct === 'cap') break;
      const ports = INTRINSIC_PORTS[ct];
      if (!ports) continue;
      const worldPorts = ports.map(p => rotateVec(p, it.rotation ?? 0));
      let inIdx = 0, inDot = -Infinity;
      for (let i = 0; i < worldPorts.length; i++) {
        const d = worldPorts[i].x * (-flowDir.x) + worldPorts[i].y * (-flowDir.y);
        if (d > inDot) { inDot = d; inIdx = i; }
      }
      let outIdx;
      if (worldPorts.length === 2) {
        outIdx = 1 - inIdx;
      } else {
        // 3-port fitting (tee): output = the non-input port best aligned with
        // forward flow. Break ties toward the more UPWARD port (-y) so a
        // side-entry tee — flow enters the branch perpendicular to the through
        // axis, leaving the two through-ports tied — exits up the chimney
        // instead of defaulting to the capped bottom leg. The bias is tiny so
        // it only decides ties, never overrides a genuinely better-aligned port.
        let outScore = -Infinity;
        for (let i = 0; i < worldPorts.length; i++) {
          if (i === inIdx) continue;
          const align = worldPorts[i].x * flowDir.x + worldPorts[i].y * flowDir.y;
          const upBias = -worldPorts[i].y * 1e-3;
          const score = align + upBias;
          if (score > outScore) { outScore = score; outIdx = i; }
        }
      }
      // A tee may bring a horizontal run into a vertical chimney, not the reverse.
      if (!teeDirectionError
          && turnsVerticalToHorizontal(ct, flowDir, worldPorts[outIdx])) {
        teeDirectionError = `Part ${it.sequenceOrder ?? '?'} is a tee turning the flow from vertical to horizontal. A tee can bring a horizontal run into a vertical chimney, not the other way round.`;
      }
      flowDir = worldPorts[outIdx];
      // No downward flow allowed: chimney gas must rise. flowDir.y > 0 means
      // the flow is heading INTO the page's positive-Y direction = canvas
      // DOWN. A tiny tolerance keeps perfectly horizontal flow (y ≈ 0) OK.
      if (flowDir.y > 0.05 && !downwardFlowError) {
        downwardFlowError = `Part ${it.sequenceOrder ?? '?'} sends the flow downward. Chimney gas must travel upward; flip or remove the offending angled fitting.`;
      }
    }
    // Signed deviation from vertical: +ve = right of vertical (clockwise in
    // canvas y-down), −ve = left. Two opposing 45° elbows give 0° here; two
    // 45° elbows in the same rotational direction give +90° (or −90°);
    // a stray 30° elbow gives ±30°.
    const finalAngleSigned = Math.atan2(flowDir.x, -flowDir.y) * 180 / Math.PI;
    if (Math.abs(finalAngleSigned) > VERTICAL_TOLERANCE_DEG) {
      const rounded = Math.round(finalAngleSigned);
      const display = rounded > 0 ? `+${rounded}` : `${rounded}`;
      errors.push(
        `The chimney exits at a ${display}° angle. Please fix.`,
      );
    }
    if (downwardFlowError) {
      errors.push(downwardFlowError);
    }
    if (teeDirectionError) {
      errors.push(teeDirectionError);
    }

    orderedForValidation.forEach((item, idx) => {
      const isFitting = item.componentType !== 'pipe-straight'
        && item.componentType !== 'chimney-straight'
        && item.componentType !== 'masonry-straight'
        && item.componentType !== 'cap';

      if (!isFitting) return;

      const hasNextPart = orderedForValidation.slice(idx + 1).some(
        p => p.componentType === 'pipe-straight' || p.componentType === 'chimney-straight' || p.componentType === 'masonry-straight',
      );

      if (!hasNextPart) {
        errors.push(`${item.name || 'Fitting'} needs a straight segment after it for a valid connection.`);
      }
    });

    // Two consecutive straight pipes with different orientations is invalid:
    // a change of direction requires an elbow or tee between them.
    for (let i = 0; i < orderedForValidation.length - 1; i++) {
      const cur = orderedForValidation[i];
      const nxt = orderedForValidation[i + 1];
      const curStraight = cur.componentType === 'pipe-straight' || cur.componentType === 'chimney-straight' || cur.componentType === 'masonry-straight';
      const nxtStraight = nxt.componentType === 'pipe-straight' || nxt.componentType === 'chimney-straight' || nxt.componentType === 'masonry-straight';
      if (curStraight && nxtStraight && cur.orientation !== nxt.orientation) {
        errors.push(
          `Part ${cur.sequenceOrder} (${cur.orientation}) and Part ${nxt.sequenceOrder} (${nxt.orientation}) have different orientations with no elbow or tee between them. Add a fitting to change orientation.`,
        );
      }
    }

    // Every part must have all its physical PORTS connected (each port either
    // touches another item's port or — for the first part — the stove). For
    // a 2-port part (straight pipe, elbow), both ports must be connected. The
    // cap has 1 port; tees have 3 but only need 2 (the side branch is often
    // left open for cleanout). The stove and the cap both count as valid
    // connection partners. Tolerance matches the snap acceptance window.
    const CONNECTION_TOL = 40; // px
    const PORT_INDEX_GROUPS = {
      'pipe-straight':    [[0, 1], [2, 3]],
      'chimney-straight': [[0, 1], [2, 3]],
      'masonry-straight': [[0, 1], [2, 3]],
      'elbow-90':         [[0, 1], [2, 3]],
      'elbow-45':         [[0, 1], [2, 3]],
      'chimney-30':       [[0, 1], [2, 3]],
      'tee':              [[0, 1], [2, 3], [4, 5]],
      'chimney-tee':      [[0, 1], [2, 3], [4, 5]],
      'masonry-tee':      [[0, 1], [2, 3], [4, 5]],
      'cap':              [[0, 1]],
    };
    const portMidpoints = (item) => {
      const eps = getItemWorldEndpoints(item);
      const groups = PORT_INDEX_GROUPS[item.componentType] || [];
      return groups.map(g => ({
        x: (eps[g[0]].x + eps[g[1]].x) / 2,
        y: (eps[g[0]].y + eps[g[1]].y) / 2,
      }));
    };
    const stovePortPts = getStoveEndpoints(
      stoveX, stoveY, CANVAS_STOVE_W, CANVAS_STOVE_H,
    );
    const allItemPorts = numberedItems.map(portMidpoints);
    numberedItems.forEach((item, i) => {
      const myPorts = allItemPorts[i];
      if (myPorts.length === 0) return;
      const otherPorts = allItemPorts.flatMap((ps, j) => j === i ? [] : ps);
      const isConnected = (p) => (
        otherPorts.some(q => Math.hypot(p.x - q.x, p.y - q.y) <= CONNECTION_TOL)
        || stovePortPts.some(s => Math.hypot(p.x - s.x, p.y - s.y) <= CONNECTION_TOL)
      );
      const connectedCount = myPorts.filter(isConnected).length;
      const ct = item.componentType;
      // Tees only need 2 of 3 ports connected (one side branch may be open).
      const needed = (ct === 'tee' || ct === 'chimney-tee' || ct === 'masonry-tee') ? 2 : myPorts.length;
      if (connectedCount < needed) {
        const partLabel = item.sequenceOrder ?? '?';
        const partName = item.name || ct;
        if (connectedCount === 0) {
          errors.push(
            `Part ${partLabel} (${partName}) is not connected to anything. Move it next to its neighbor.`,
          );
        } else {
          errors.push(
            `Part ${partLabel} (${partName}) is missing a connection. Every part needs both an inlet and an outlet (the stove or cap counts).`,
          );
        }
      }
    });

    const missingVentTypeNumbers = orderedForValidation
      .filter(item =>
        (item.componentType === 'pipe-straight' || item.componentType === 'chimney-straight' || item.componentType === 'masonry-straight')
        && !findVentOption(item.ventOptionId),
      )
      .map(item => item.sequenceOrder)
      .filter(n => typeof n === 'number');

    // A system uses one type per category throughout. Only checked once every part
    // has a type, so a half-finished design reports the missing type rather than
    // complaining about a mix it cannot yet judge.
    if (missingVentTypeNumbers.length === 0) {
      errors.push(...mixedTypeErrors(orderedForValidation));
    }

    // Stove pipe connects the appliance to the chimney; once the run is in chimney
    // or masonry it cannot revert.
    errors.push(...stovePipeAfterChimneyErrors(orderedForValidation));

    if (missingVentTypeNumbers.length > 0) {
      errors.push(`Please select part type for part(s) ${missingVentTypeNumbers.join(', ')}.`);
    }

    const missingTempNumbers = orderedForValidation
      .filter(item => !Number.isFinite(item.ambientTempF))
      .map(item => item.sequenceOrder)
      .filter(n => typeof n === 'number');

    if (missingTempNumbers.length > 0) {
      errors.push(`Please specify temperature of part(s) ${missingTempNumbers.join(', ')}.`);
    }

    if (errors.length > 0) {
      setOutputError(errors.join('\n'));
      return;
    }

    const fToK = (f) => ((f - 32) * 5 / 9) + 273.15;
    const parseTempF = (raw, fallback) => {
      const n = Number(raw);
      return Number.isFinite(n) ? n : fallback;
    };
    const indoorDefaultK = fToK(parseTempF(indoorTempF, 65));
    const outdoorDefaultK = fToK(parseTempF(outdoorTempF, 35));

    const ordered = [...numberedItems]
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      .map(item => ({ ...item }));

    const isStraight = (item) => item.componentType === 'pipe-straight' || item.componentType === 'chimney-straight' || item.componentType === 'masonry-straight';
    const toRounded = (n, digits = 6) => Number(n.toFixed(digits));
    const getDiameterIn = (item) => Number(item.selectedDiameter || item.diameters?.[0] || 6);
    const getDiameterFt = (item) => toRounded(getDiameterIn(item) / 12, 6);
    const getHeightFt = (item) => Number(item.length ?? item.height ?? 3);
    const getAmbientK = (item) => {
      const itemAmbientK = Number.isFinite(item.ambientTempF) ? fToK(item.ambientTempF) : null;
      const defaultAmbientK = item.isOutdoor ? outdoorDefaultK : indoorDefaultK;
      return Number((itemAmbientK ?? defaultAmbientK).toFixed(2));
    };

    const partItems = ordered.filter(isStraight);
    const parts = partItems.map((item) => {
      // Backend only understands 'vertical' or 'horizontal'. 30°/45° pipes
      // round to 'vertical' (closest physical sense — they have vertical rise).
      const orientation = item.orientation === 'horizontal' ? 'horizontal' : 'vertical';
      const height_ft = getHeightFt(item);

      // Look up the selected vent option (if any). When present, its
      // simulator_type and per-layer material properties drive the payload;
      // explicit per-item overrides (set by Advanced editing) still win.
      const ventOpt = findVentOption(item.ventOptionId);
      const inferredType = (item.part_type || item.type || '').toLowerCase();
      const partType =
        ventOpt?.simulator_type ||
        (inferredType === 'single' || inferredType === 'triple' ? inferredType : null) ||
        (item.componentType === 'chimney-straight' || item.componentType === 'masonry-straight' || item.isOutdoor ? 'triple' : 'single');

      const base = {
        _name: item.name,
        type: partType,
        inner_diameter_ft: getDiameterFt(item),
        height_ft,
        z_ft: Number(item.z_ft ?? 0),
        orientation,
        T_ambient_K: getAmbientK(item),
      };

      if (partType === 'single') {
        const inner = ventOpt?.inner;
        return {
          ...base,
          Pa: Number(item.Pa ?? 98457),
          num_segments: Number(item.num_segments ?? Math.max(1, Math.round(height_ft))),
          thickness_w_m: Number(item.thickness_w_m ?? inner?.thickness_m ?? 0.00076),
          density_w_kg_m3: Number(item.density_w_kg_m3 ?? inner?.density_kg_m3 ?? 7850),
          cp_w_J_kg_K: Number(item.cp_w_J_kg_K ?? inner?.cp_J_kg_K ?? 460),
          k_w_W_m_K: Number(item.k_w_W_m_K ?? inner?.k_W_m_K ?? 50),
          epsilon_w: Number(item.epsilon_w ?? inner?.emissivity ?? 0.8),
          roughness_m: Number(item.roughness_m ?? inner?.roughness_m ?? 0.00015),
        };
      }

      const inner = ventOpt?.inner;
      const middle = ventOpt?.middle;
      const outer = ventOpt?.outer;
      return {
        ...base,
        Pa: Number(item.Pa ?? 98596),
        num_segments: Number(item.num_segments ?? (orientation === 'horizontal' ? 2 : Math.max(1, Math.round(height_ft)))),
        thickness_inner_m: Number(item.thickness_inner_m ?? inner?.thickness_m ?? 0.0005),
        density_inner_kg_m3: Number(item.density_inner_kg_m3 ?? inner?.density_kg_m3 ?? 7850),
        cp_inner_J_kg_K: Number(item.cp_inner_J_kg_K ?? inner?.cp_J_kg_K ?? 460),
        k_inner_W_m_K: Number(item.k_inner_W_m_K ?? inner?.k_W_m_K ?? 26),
        epsilon_inner: Number(item.epsilon_inner ?? inner?.emissivity ?? 0.35),
        thickness_middle_m: Number(item.thickness_middle_m ?? middle?.thickness_m ?? 0.025),
        density_middle_kg_m3: Number(item.density_middle_kg_m3 ?? middle?.density_kg_m3 ?? 100),
        cp_middle_J_kg_K: Number(item.cp_middle_J_kg_K ?? middle?.cp_J_kg_K ?? 1050),
        k_middle_W_m_K: Number(item.k_middle_W_m_K ?? middle?.k_W_m_K ?? 0.04),
        thickness_outer_m: Number(item.thickness_outer_m ?? outer?.thickness_m ?? 0.0005),
        density_outer_kg_m3: Number(item.density_outer_kg_m3 ?? outer?.density_kg_m3 ?? 8000),
        cp_outer_J_kg_K: Number(item.cp_outer_J_kg_K ?? outer?.cp_J_kg_K ?? 500),
        k_outer_W_m_K: Number(item.k_outer_W_m_K ?? outer?.k_W_m_K ?? 16),
        epsilon_outer: Number(item.epsilon_outer ?? outer?.emissivity ?? 0.35),
        air_gap_inner_m: Number(item.air_gap_inner_m ?? 0.0),
        air_gap_outer_m: Number(item.air_gap_outer_m ?? 0.0),
        roughness_m: Number(item.roughness_m ?? inner?.roughness_m ?? 1e-6),
      };
    });

    const getPrevPartIndex = (sequence) => {
      for (let i = partItems.length - 1; i >= 0; i -= 1) {
        if (partItems[i].sequenceOrder < sequence) return i;
      }
      return -1;
    };

    const getNextPartIndex = (sequence) => {
      for (let i = 0; i < partItems.length; i += 1) {
        if (partItems[i].sequenceOrder > sequence) return i;
      }
      return -1;
    };

    // Tag every fitting with its [prev_straight, next_straight] pair, then
    // group fittings that share the same pair. The backend rejects multiple
    // elbows at the same junction, so we emit ONE combined entry per group:
    // K-loss is treated as additive over a series of bends, so the combined
    // angle_deg is the sum of absolute angles in the group. (Net direction
    // of the path is enforced separately by the vertical-end check.)
    const fittingsTagged = ordered
      .filter(item => !isStraight(item) && item.componentType !== 'cap')
      .map(item => {
        const prevPartIdx = getPrevPartIndex(item.sequenceOrder);
        const nextPartIdx = getNextPartIndex(item.sequenceOrder);
        return {
          item,
          between_parts: [prevPartIdx, nextPartIdx >= 0 ? nextPartIdx : prevPartIdx],
        };
      });

    const fittingGroups = new Map();
    for (const f of fittingsTagged) {
      const key = f.between_parts.join(',');
      if (!fittingGroups.has(key)) fittingGroups.set(key, []);
      fittingGroups.get(key).push(f);
    }

    // Mirrors flued_module/flow/elbows.py::base_K for commercial_steel pipe
    // (the only smoothness the frontend sends). Used to pre-sum K when an
    // elbow shares a junction with a tee — the engine accepts only ONE entry
    // per junction, so we collapse them and pass K_tee + K_elbow as K_override.
    const BASE_K_TABLE = {
      long_radius_90:  { R: [1.0, 1.5, 2.5, 5.0], K: [0.80, 0.40, 0.30, 0.25] },
      long_radius_45:  { R: [1.0, 1.5, 2.5, 5.0], K: [0.40, 0.20, 0.15, 0.12] },
      short_radius_90: { R: [0.8, 1.0, 1.5],      K: [1.90, 1.60, 1.00] },
      short_radius_45: { R: [0.8, 1.0, 1.5],      K: [0.95, 0.80, 0.50] },
      mitered_90:      { R: [1.0],                K: [2.40] },
      tee_90:          { R: [1.0],                K: [1.00] },
    };
    const interpK = (R, Rs, Ks) => {
      if (R <= Rs[0]) return Ks[0];
      if (R >= Rs[Rs.length - 1]) return Ks[Ks.length - 1];
      for (let i = 0; i < Rs.length - 1; i += 1) {
        if (R >= Rs[i] && R <= Rs[i + 1]) {
          const t = (R - Rs[i]) / (Rs[i + 1] - Rs[i]);
          return Ks[i] + t * (Ks[i + 1] - Ks[i]);
        }
      }
      return Ks[Ks.length - 1];
    };
    const baseK = (elbowType, angleDeg, R_over_D) => {
      const refAngle = angleDeg <= 45 ? 45 : 90;
      const key = `${elbowType}_${refAngle}`;
      const tbl = BASE_K_TABLE[key] || BASE_K_TABLE.short_radius_90;
      const usedRef = BASE_K_TABLE[key] ? refAngle : 90;
      let K = interpK(R_over_D, tbl.R, tbl.K) * (angleDeg / usedRef);
      if (angleDeg > 90) K *= 1.0 + 0.3 * ((angleDeg - 90) / 90);
      return Math.max(0, K);
    };

    const elbows = [];
    for (const group of fittingGroups.values()) {
      const first = group[0].item;
      const between_parts = group[0].between_parts;
      const diameter_ft = getDiameterFt(first);
      const ambientK = getAmbientK(first);
      const isTeeCT = (ct) => ct === 'tee' || ct === 'chimney-tee' || ct === 'masonry-tee';
      const hasTee = group.some(f => isTeeCT(f.item.componentType));

      if (hasTee) {
        // Tee carries the junction geometry (vertical/horizontal lengths).
        const tee = group.find(f => isTeeCT(f.item.componentType)).item;
        const teeEntry = {
          type: 'tee',
          between_parts,
          diameter_ft,
          vertical_length_ft: Number(tee.vertical_length_ft ?? 1.0),
          horizontal_length_ft: Number(tee.horizontal_length_ft ?? 0.5417),
          T_ambient_K: ambientK,
        };

        // If one or more elbows share this junction (no straight pipe
        // between them and the tee), the engine only accepts a single
        // fitting per junction, so pre-sum the K-losses and override.
        //
        // Why the 2.0 multiplier: K_override bypasses the engine's
        // transitional-Re correction in elbows.py (min(1 + 500/sqrt(Re), 2.0)
        // for 2300 < Re < 2e4). Wood-heater flue gas runs at Re ≈ 3k–10k,
        // where that correction is always saturated at the 2.0 cap. Without
        // it, an elbow adjacent to a tee would read as half the pressure
        // drop of the same elbow with a pipe between it and the tee — the
        // wrong direction. The 2.0× restores parity for the typical regime.
        const nonTeeFittings = group.filter(f => !isTeeCT(f.item.componentType));
        if (nonTeeFittings.length > 0) {
          const K_tee = baseK('tee', 90, 1.0);
          const K_elbows = nonTeeFittings.reduce((sum, f) => {
            const ct = f.item.componentType;
            const angle = ct === 'elbow-45' ? 45
              : ct === 'chimney-30' ? 30
              : Number(f.item.angle_deg ?? 90);
            const elbowType = f.item.elbow_type || 'long_radius';
            const R_over_D = Number(f.item.R_over_D ?? 1.5);
            return sum + baseK(elbowType, angle, R_over_D);
          }, 0);
          const TRANSITIONAL_RE_FACTOR = 2.0;
          teeEntry.K_override = TRANSITIONAL_RE_FACTOR * (K_tee + K_elbows);
        }

        elbows.push(teeEntry);
        continue;
      }

      const totalAngle = group.reduce((sum, f) => {
        const a = f.item.componentType === 'elbow-45'
          ? 45
          : f.item.componentType === 'chimney-30'
            ? 30
            : Number(f.item.angle_deg ?? 90);
        return sum + Math.abs(a);
      }, 0);

      elbows.push({
        type: 'elbow',
        between_parts,
        diameter_ft,
        height_ft: Number(first.height_ft ?? 0.625),
        width_ft: Number(first.width_ft ?? 0.7083),
        angle_deg: totalAngle,
        R_over_D: Number(first.R_over_D ?? 1.5),
        elbow_type: first.elbow_type || 'long_radius',
        T_ambient_K: ambientK,
      });
    }

    const payload = buildSimulationPayload({
      parts,
      elbows,
      elevationFt,
      depressurizationPa,
      selectedFireProfile,
      customProfile,
      fireProfiles: FIRE_PROFILES,
    });
    setShowComponentList(false);
    setPayloadText(JSON.stringify(payload, null, 2));

    // Above the viability cutoff the verdict is fixed, so there is nothing for the
    // simulation to tell us. Skip the round trip and report it immediately.
    if (isDesignNotViable(depressurizationPa)) {
      setLastResult(null);
      setShowResults(true);
      setOutputError(null);
      setOutputStatus(null);
      return;
    }

    setOutputStatus('Calculating...');

    try {
      await runSimulation(payload);
    } catch (err) {
      setOutputStatus(null);
      setOutputError(`Finalize succeeded, but calculation failed: ${err.message}`);
    }
  };

  return (
    <div className="App">
      {!licenseAccepted && (
        <LicenseModal onAgree={handleLicenseAgree} />
      )}
      {customModalStep && (
        <CustomProfileModal
          initialStep={customModalStep}
          onClose={() => setCustomModalStep(null)}
          onUse={handleUseCustomProfile}
          onSeeInstructions={handleSeeCustomInstructions}
        />
      )}
      <header className="App-header">
        <div className="app-top-bar">
          <h1>MC Chimney Draft Tool</h1>
          {isMobile ? (
            <MobileMenu activeTab={activeTab} setActiveTab={setActiveTab} />
          ) : (
          <nav className="tab-nav">
            <button
              className={`tab-btn${activeTab === 'workspace' ? ' active' : ''}`}
              onClick={() => setActiveTab('workspace')}
            >Workspace</button>
            <button
              className={`tab-btn${activeTab === 'guide' ? ' active' : ''}`}
              onClick={() => setActiveTab('guide')}
            >User Guide</button>
            <button
              className={`tab-btn${activeTab === 'settings' ? ' active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >Settings</button>
            <button
              className={`tab-btn${activeTab === 'profile' ? ' active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >Profile</button>
            <button
              className={`tab-btn${activeTab === 'about' ? ' active' : ''}`}
              onClick={() => setActiveTab('about')}
            >About</button>
          </nav>
          )}
        </div>

        {activeTab === 'workspace' && (
          isMobile ? (
            <MobileWorkspace
              canvasItems={canvasItems}
              setCanvasItems={setCanvasItems}
              selectedItemIndex={selectedItemIndex}
              setSelectedItemIndex={setSelectedItemIndex}
              showStoveMenu={showStoveMenu}
              setShowStoveMenu={setShowStoveMenu}
              doorAngle={doorAngle}
              setDoorAngle={handleDoorAngleChange}
              indoorTempF={indoorTempF}
              setIndoorTempF={setIndoorTempF}
              outdoorTempF={outdoorTempF}
              setOutdoorTempF={setOutdoorTempF}
              elevationFt={elevationFt}
              setElevationFt={setElevationFt}
              stoveX={stoveX}
              setStoveX={setStoveX}
              setStoveY={setStoveY}
              roofOutdoorOverhang={roofOutdoorOverhang}
              setRoofOutdoorOverhang={setRoofOutdoorOverhang}
              wallTopY={wallTopY}
              setWallTopY={setWallTopY}
              onAddPart={handleAddPart}
              onFinalizeDesign={handleFinalizeDesign}
              finalizeDisabled={!canFinalizeDesign || outputStatus === 'Calculating...'}
              handleClearDesign={handleClearDesign}
              selectedFireProfile={selectedFireProfile}
              fireProfileOptions={[
                ...Object.entries(FIRE_PROFILES).map(([value, cfg]) => ({ value, label: cfg.label })),
                { value: 'custom', label: customProfile ? 'Custom (loaded)' : 'Custom…' },
              ]}
              onFireProfileChange={handleFireProfileChange}
              customProfileName={selectedFireProfile === 'custom' ? customProfile?.name : null}
              onEditCustomProfile={() => setCustomModalStep('upload')}
              depressurizationPa={depressurizationPa}
              setDepressurizationPa={setDepressurizationPa}
              templateOptions={TEMPLATE_OPTIONS}
              onInsertTemplate={handleInsertTemplate}
              outputError={outputError}
              outputStatus={outputStatus}
              hasResults={!!lastResult}
              showResults={showResults}
              setShowResults={setShowResults}
              lastResult={lastResult}
              resultUnits={resultUnits}
              setResultUnits={setResultUnits}
              handleVentTypeChange={handleVentTypeChange}
              handleDiametersChange={handleDiametersChange}
              handleHeightChange={handleHeightChange}
              handleAmbientTempChange={handleAmbientTempChange}
              handleOrientationChange={handleOrientationChange}
              availableOrientations={getAvailableOrientations(canvasItems[selectedItemIndex])}
              handleSequenceOrderChange={handleSequenceOrderChange}
              handleDelete={handleDelete}
            />
          ) : (
        <div className={`workspace-container${showResults ? ' workspace-results-mode' : ''}`}>
          {showResults ? (
            /* ── Results mode: collapsed design preview + results panel ── */
            <>
              <div className="design-preview-panel">
                <h3 className="design-preview-title">Current Design</h3>
                <div style={{ width: '100%', flex: '1 1 0', minHeight: 0, overflow: 'hidden', position: 'relative', pointerEvents: 'none' }}>
                  <Canvas
                    items={canvasItems}
                    setItems={setCanvasItems}
                    selectedItemIndex={null}
                    setSelectedItemIndex={() => {}}
                    showStoveMenu={false}
                    setShowStoveMenu={() => {}}
                    doorAngle={doorAngle}
                    setDoorAngle={() => {}}
                    hideTitle
                    readOnly
                    fullWidth
                    indoorTempF={indoorTempF}
                    setIndoorTempF={setIndoorTempF}
                    outdoorTempF={outdoorTempF}
                    setOutdoorTempF={setOutdoorTempF}
                    elevationFt={elevationFt}
                    setElevationFt={setElevationFt}
                    stoveX={stoveX}
                    onStoveXChange={setStoveX}
                    onStoveYChange={setStoveY}
                    roofOutdoorOverhang={roofOutdoorOverhang}
                    onRoofOutdoorOverhangChange={setRoofOutdoorOverhang}
                    wallTopY={wallTopY}
                    onWallTopYChange={setWallTopY}
                  />
                </div>
                <button className="edit-design-btn" onClick={() => setShowResults(false)}>
                  Edit Design
                </button>
              </div>
              <div className="results-area">
                <ResultsPanel
                  result={lastResult}
                  units={resultUnits}
                  onUnitsChange={setResultUnits}
                  depressurizationPa={Math.abs(Number(depressurizationPa) || 0)}
                />
              </div>
            </>
          ) : (
            /* ── Normal editing mode ── */
            <>
              <Sidebar
                items={canvasItems}
                showComponentList={showComponentList}
                setShowComponentList={setShowComponentList}
                handleClearDesign={handleClearDesign}
                showStoveMenu={showStoveMenu}
                setShowStoveMenu={setShowStoveMenu}
                doorAngle={doorAngle}
                setDoorAngle={handleDoorAngleChange}
                hasResults={!!lastResult}
                onShowResults={() => setShowResults(true)}
                finalizeDisabled={!canFinalizeDesign}
                finalizeTitle={canFinalizeDesign
                  ? 'Finalize design and run calculation'
                  : 'Drop at least one part on the canvas before finalizing.'}
                selectedFireProfile={selectedFireProfile}
                fireProfileOptions={[
                  ...Object.entries(FIRE_PROFILES).map(([value, cfg]) => ({ value, label: cfg.label })),
                  { value: 'custom', label: customProfile ? 'Custom (loaded)' : 'Custom…' },
                ]}
                onFireProfileChange={handleFireProfileChange}
                customProfileName={selectedFireProfile === 'custom' ? customProfile?.name : null}
                onEditCustomProfile={() => setCustomModalStep('upload')}
                onFinalizeDesign={handleFinalizeDesign}
                elevationFt={elevationFt}
                setElevationFt={setElevationFt}
                depressurizationPa={depressurizationPa}
                setDepressurizationPa={setDepressurizationPa}
                templateOptions={TEMPLATE_OPTIONS}
                onInsertTemplate={handleInsertTemplate}
              />
              <Canvas
                items={canvasItems}
                setItems={setCanvasItems}
                selectedItemIndex={selectedItemIndex}
                setSelectedItemIndex={setSelectedItemIndex}
                showStoveMenu={showStoveMenu}
                setShowStoveMenu={setShowStoveMenu}
                doorAngle={doorAngle}
                setDoorAngle={handleDoorAngleChange}
                indoorTempF={indoorTempF}
                setIndoorTempF={setIndoorTempF}
                outdoorTempF={outdoorTempF}
                setOutdoorTempF={setOutdoorTempF}
                elevationFt={elevationFt}
                setElevationFt={setElevationFt}
                stoveX={stoveX}
                onStoveXChange={setStoveX}
                onStoveYChange={setStoveY}
                roofOutdoorOverhang={roofOutdoorOverhang}
                onRoofOutdoorOverhangChange={setRoofOutdoorOverhang}
                wallTopY={wallTopY}
                onWallTopYChange={setWallTopY}
              />
              <Output
                items={canvasItems}
                showComponentList={showComponentList}
                setShowComponentList={setShowComponentList}
                onPresetSelect={handlePresetSelect}
                payloadText={payloadText}
                setPayloadText={setPayloadText}
                outputError={outputError}
                outputStatus={outputStatus}
                isCalculating={outputStatus === 'Calculating...'}
                selectedItemIndex={selectedItemIndex}
                setSelectedItemIndex={setSelectedItemIndex}
                handleDiametersChange={handleDiametersChange}
                handleHeightChange={handleHeightChange}
                handleAmbientTempChange={handleAmbientTempChange}
                handleOrientationChange={handleOrientationChange}
                handleVentTypeChange={handleVentTypeChange}
                availableOrientations={getAvailableOrientations(canvasItems[selectedItemIndex])}
                handleSequenceOrderChange={handleSequenceOrderChange}
                handleDelete={handleDelete}
                handleClearDesign={handleClearDesign}
                onFinalizeDesign={handleFinalizeDesign}
                finalizeDisabled={!canFinalizeDesign || outputStatus === 'Calculating...'}
                finalizeTitle={canFinalizeDesign
                  ? 'Finalize design and run calculation'
                  : 'Drop at least one part on the canvas before finalizing.'}
              />
            </>
          )}
        </div>
          )
        )}

        {activeTab === 'guide' && (
          <div className="tab-content-panel">
            <nav className="about-subtab-nav">
              <button
                className={`about-subtab-btn${guideSubtab === 'guide' ? ' active' : ''}`}
                onClick={() => setGuideSubtab('guide')}
              >Guide</button>
              <button
                className={`about-subtab-btn${guideSubtab === 'assumptions' ? ' active' : ''}`}
                onClick={() => setGuideSubtab('assumptions')}
              >Assumptions</button>
            </nav>
            <div className="about-subtab-content">
              {guideSubtab === 'guide' && (
                <div className="user-guide">
            <h2>User Guide</h2>
            <p>
              The MC Chimney Draft Tool estimates how well a wood heater and chimney
              installation will draft, based on the parameters you provide. Starting from
              the stove, you lay out stovepipe and chimney components, and the tool reports
              draft results, peak static pressure, and peak flue-gas temperatures over time.
            </p>
            <p>
              Source code is available on{' '}
              <a href="https://github.com/LBNL-Combustion/mc-draft-tool" target="_blank" rel="noopener noreferrer">
                GitHub
              </a>.
            </p>

            <p>
              <strong>First visit only:</strong> read the agreement and click{' '}
              <strong>I Agree</strong> to enter the tool.
            </p>

            <h3>Quick start</h3>
            <ol>
              <li>
                Drag parts from the left palette onto the canvas, starting at the stove.
                <div className="user-guide-or">-OR-</div>
                Pick a <strong>Template</strong> (Straight / Bent / Lined Chimney) and click <strong>Insert</strong>.
              </li>
              <li>Connect them end-to-end (parts snap together) and finish with a Cap at the top.</li>
              <li>Click each part and set its type, Diameter, Length, and Surrounding Temp on the right.</li>
              <li>Set Elevation.</li>
              <li>Set Room Depressurization (see below).</li>
              <li>Set Fire Profile (see below).</li>
              <li>Click Finalize Design to validate and run, then read the Results.</li>
            </ol>

            <h3>Left: parts and conditions</h3>
            <p><strong>Templates</strong>: pick one from the dropdown and click <strong>Insert</strong> to auto-populate the canvas with a ready-made layout.</p>
            <ul>
              <li><strong>Straight Chimney</strong>: 8 ft single-wall stove pipe → 7 ft Class A stainless → cap.</li>
              <li><strong>Bent Chimney</strong>: vertical stove pipe → 90° elbow → short horizontal Class A → chimney tee → vertical Class A → cap.</li>
              <li><strong>Lined Chimney</strong>: masonry tee off the stove side → 15 ft vertical lined-masonry with vermiculite → cap (tee bottom leg open as cleanout).</li>
            </ul>
            <p>Inserting a template replaces whatever is on the canvas. You can then edit any part normally.</p>
            <p>Drag any of these parts onto the canvas (or use a template):</p>
            <table className="user-guide-table">
              <thead>
                <tr><th>Group</th><th>Parts</th></tr>
              </thead>
              <tbody>
                <tr><td>Stove Pipes</td><td>Straight, Elbow 90°, Elbow 45°, Tee</td></tr>
                <tr><td>Chimneys</td><td>Straight, Tee, Elbow 30°</td></tr>
                <tr><td>Lined Masonry Chimneys</td><td>Straight, Tee. Liner Type options: uninsulated flex liner, uninsulated rigid liner, liner + vermiculite, liner + mineral wool</td></tr>
                <tr><td>Termination</td><td>Cap (required at the very top)</td></tr>
              </tbody>
            </table>
            <ul>
              <li><strong>Elevation (ft)</strong>: site elevation; adjusts the ambient air pressure used by the simulation.</li>
              <li><strong>Room Depressurization (Pa)</strong>: pressure deficit of the room relative to outdoors (e.g. from a competing exhaust fan). <strong>Above 2.5 Pa the design is flagged not viable</strong> and the Results page shows a failure verdict instead of charts.</li>
              <li><strong>Fire Profile</strong>: <strong>Low</strong>, <strong>Medium</strong>, or <strong>High</strong> burn intensity (preset inlet-gas temperature curves), or <strong>Custom…</strong> to upload your own (see below).</li>
            </ul>

            <h3>Middle: canvas</h3>
            <ul>
              <li>The stove is anchored at the bottom; drag it left/right to reposition.</li>
              <li>Parts snap together at their connection points; an angled fitting auto-rotates the pipe that attaches to it.</li>
              <li>Double-click a part to rotate it (straight pipes flip between vertical and horizontal).</li>
              <li>Select a straight pipe and drag the blue arrow to change its length (1-ft steps).</li>
              <li>Parts are automatically tagged indoor or outdoor based on where they sit relative to the wall/roof.</li>
            </ul>

            <h3>Right: part settings</h3>
            <p>Click a part to edit:</p>
            <ul>
              <li>
                <strong>Stove Pipe Type / Chimney Type / Liner Type</strong>: the label matches
                the part you selected, and the choices are filtered to that category.
                <ul>
                  <li><strong>Stove Pipe Types</strong>: Single-wall Steel, Single-wall Stainless, Double-wall.</li>
                  <li><strong>Chimney Types</strong>: Air Cooled, Class A (Stainless), Triple-wall.</li>
                  <li><strong>Liner Types</strong>: Uninsulated (flex liner), Uninsulated (rigid liner), Vermiculite insulation, Mineral wool insulation.</li>
                </ul>
              </li>
              <li><strong>Diameter</strong>: 3–8 in.</li>
              <li><strong>Length</strong>: straight pipes only (ft).</li>
              <li><strong>Orientation</strong>: toggle vertical / horizontal.</li>
              <li><strong>Surrounding Temp</strong>: ambient temperature around that part (°F). If left blank, the part inherits a default based on whether the canvas tags it indoor (≈65 °F) or outdoor (≈35 °F). Masonry parts always use the outdoor default at the brick boundary.</li>
              <li><strong>Part Number</strong>: assigned automatically along the path from stove to cap; override if needed.</li>
              <li><strong>Delete Component</strong>: removes the component from the canvas.</li>
            </ul>

            <h3>Finalize and run</h3>
            <p>Click Finalize Design. The tool validates the layout and lists anything to fix, for example:</p>
            <ul>
              <li>every part is connected (each needs both an inlet and an outlet),</li>
              <li>exactly one Cap at the top,</li>
              <li>the chimney exits vertically (no sideways or downward exit),</li>
              <li>a Vent Type and Surrounding Temp are set on every part,</li>
              <li>part numbering is valid with no gaps.</li>
            </ul>
            <p>
              Fix anything it flags as an error, then click Finalize again. Once it passes, the simulation runs.
              A base Tee used as a cleanout only needs 2 of its 3 ports connected, so leaving
              one branch open is fine.
            </p>

            <h3>Results</h3>
            <ul>
              <li><strong>Risk of Backdraft</strong> badge: low / medium / high.</li>
              <li><strong>Max Static Pressure</strong>: the peak value over the whole simulation.</li>
              <li>Charts: Chimney Exhaust Temperature and Static Pressure at chimney entrance, over time.</li>
              <li>Toggle Metric / Imperial units (top-right of Results).</li>
              <li><strong>Edit Design</strong> button returns you to the canvas with your layout intact.</li>
              <li>If <strong>Room Depressurization &gt; 2.5 Pa</strong>, Results shows a &quot;Design not viable&quot; verdict in place of the badges and charts. It is recommended to fix the room pressure of the installation (e.g. add make-up air to the room) and recheck pressure before re-running the Chimney Draft Tool.</li>
            </ul>

            <h3>Phone / mobile use</h3>
            <p>
              On phones the tool switches to a touch-friendly layout: tap a part in
              the <strong>+ Add</strong> sheet to place it, pinch and pan to navigate the canvas,
              tap a part to open a bottom-sheet editor, and use the
              <strong> Setup</strong> button for Templates, Fire Profile, Elevation, and
              Room Depressurization. Tap <strong>Run</strong> to validate and simulate.
              The simulation, validation, and results are the same as on desktop.
            </p>

            <h3>Custom fire profile</h3>
            <p>
              Instead of Low / Medium / High, you can upload your own inlet-gas
              temperature curve: open the <strong>Fire Profile</strong> dropdown and
              choose <strong>Custom…</strong>.
            </p>
            <ul>
              <li>Accepted files: <strong>CSV</strong> or <strong>JSON</strong>.</li>
              <li>Each point has two values: <strong>time</strong> and <strong>temperature</strong>.</li>
              <li>Choose your units in the upload window: time in minutes or seconds; temperature in °C, °F, or K.</li>
              <li>The first time must be <strong>0</strong> and times must increase. Cover at least 0–10 minutes (a shorter profile holds its last value).</li>
            </ul>
            <p><strong>CSV</strong> (two columns, header optional):</p>
            <pre className="cp-code">{`time_min,temp_C
0,20
2,40
3.9,600
5,470
10,420`}</pre>
            <p><strong>JSON</strong> (any of these shapes):</p>
            <pre className="cp-code">{`[[0,20],[2,40],[3.9,600],[10,420]]

{"time":[0,2,3.9,10],"temperature":[20,40,600,420]}

[{"time":0,"temp":20},{"time":10,"temp":420}]`}</pre>
            <p>
              The upload window shows a live preview and flags any formatting
              problems before you apply the profile.
            </p>

            <h3>Other tabs</h3>
            <ul>
              <li><strong>Settings</strong> (advanced): view/edit the parts and vent-material catalog.</li>
              <li><strong>About</strong>: Our Team, Acknowledgements, and Disclaimers.</li>
            </ul>

            <p className="user-guide-tip">
              <em>
                Tip: start simple (stove &rarr; straight pipe &rarr; optional elbow &rarr;
                chimney &rarr; cap), get a clean result, then add complexity.
              </em>
            </p>
                </div>
              )}
              {guideSubtab === 'assumptions' && (
                <Assumptions fireProfiles={FIRE_PROFILES} doorSchedule={DEFAULT_DOOR_SCHEDULE} />
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="tab-content-panel">
            <Settings />
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="tab-content-panel">
            <h2>Profile</h2>
            <p>User profile information will appear here.</p>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="tab-content-panel">
            <nav className="about-subtab-nav">
              <button
                className={`about-subtab-btn${aboutSubtab === 'team' ? ' active' : ''}`}
                onClick={() => setAboutSubtab('team')}
              >Our Team</button>
              <button
                className={`about-subtab-btn${aboutSubtab === 'acknowledgements' ? ' active' : ''}`}
                onClick={() => setAboutSubtab('acknowledgements')}
              >Acknowledgements</button>
              <button
                className={`about-subtab-btn${aboutSubtab === 'disclaimers' ? ' active' : ''}`}
                onClick={() => setAboutSubtab('disclaimers')}
              >Disclaimers</button>
            </nav>
            <div className="about-subtab-content">
              {aboutSubtab === 'team' && (
                <div className="about-team">
                  {TEAM.map(member => (
                    <div className="team-member" key={member.name}>
                      {member.photo ? (
                        <img className="team-photo" src={member.photo} alt={member.name} />
                      ) : (
                        <div className="team-photo team-photo-placeholder">
                          {member.name.split(' ').map(w => w[0]).join('')}
                        </div>
                      )}
                      <div className="team-name">{member.name}</div>
                    </div>
                  ))}
                </div>
              )}
              {aboutSubtab === 'acknowledgements' && (
                <div className="about-acknowledgements">
                  <p>
                    This work was supported by the Bioenergy Technologies Office of the
                    U.S. Department of Energy under Contract No. DE-AC02-05CH11231.
                  </p>
                  <p>
                    We would also like to acknowledge the following collaborators for their
                    contributions to this project:
                  </p>
                  <ul>
                    {[
                      'Eric Adair',
                      'John Crouch',
                      'Bob Ferrari',
                      'Ryan Fisher',
                      'Ben Myren',
                      'Chris Neufeld',
                      'Corie Podschelne',
                      'Rick Vlahos',
                    ].map(name => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {aboutSubtab === 'disclaimers' && (
                <div className="about-disclaimer-text">{DISCLAIMERS_TEXT}</div>
              )}
            </div>
          </div>
        )}
      </header>
      <footer className="app-footer">
        <img src={lblLogo} alt="Lawrence Berkeley National Laboratory" />
        <div className="app-footer-text">
          <div className="app-footer-funding">
            This work was supported by the Bioenergy Technologies Office of the U.S. Department of Energy under Contract No. DE-AC02-05CH11231.
          </div>
          <div className="app-footer-meta">
            <span>
              Copyright &copy; 2026 The Regents of the University of California, through<br />Lawrence Berkeley National Laboratory. All rights reserved. For more information click{' '}
              <button
                type="button"
                className="app-footer-link"
                onClick={() => { setActiveTab('about'); setAboutSubtab('disclaimers'); }}
              >here</button>
            </span>
            <span className="app-footer-version">Wood Heater Tool v1.5.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
